import { Dialog, DialogTitle } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSimulators } from '../../api';
import { hasGroupSimulationAccess } from '../../auth';
import CreateExerciseError from '../../create-exercise-error/CreateExerciseError';
import { ExerciseFile, ExerciseMetaData, ExerciseType } from '../../types';
import CreateExerciseActions from '../CreateExerciseActions';
import CreateExerciseStepper from '../CreateExerciseStepper';
import { useCreateExercise } from '../useCreateExercise';
import CautionStep from './CautionStep';
import ExerciseStep from './ExerciseStep';
import OverviewStep from './OverviewStep';
import StudentsStep from './StudentsStep';
import TypeStep from './TypeStep';

export interface CreateExerciseDialogProps {
  onClose: (reason?: string) => void;
  onConfirm: () => void;
  open: boolean;
}

export interface ExerciseFormData {
  adHoc: boolean;
  collectionId: string | null;
  description: string;
  endTime: Date | null;
  exercises: ExerciseFile[] | null;
  exerciseId: string | null;
  exercisesIds: string[] | null;
  exercisesMetaData: ExerciseMetaData[] | null;
  layout: string | null;
  limitNumberOfAttempts: boolean;
  maxNumberOfAttempts: string;
  moduleId: string | null;
  studentEnvironment: { name: string; position?: string } | null;
  simulator: string;
  startTime: Date | null;
  timeRange?: string; // Virtual field for setting errors
  timezone: string;
  title: string;
  type: ExerciseType;
  users: string[];
  visibility: boolean;
  vessel: {
    vesselCode: string | null;
    vesselName: string | null;
  } | null;
}

const selfStudySteps: { fields: Array<keyof ExerciseFormData>; label: string }[] = [
  { fields: [], label: 'Type' },
  { fields: ['title', 'description', 'timezone', 'startTime', 'endTime'], label: 'Overview' },
  { fields: ['simulator', 'exercisesIds', 'visibility'], label: 'Exercise' },
  { fields: ['users'], label: 'Students' },
];
const classroomSteps: { fields: Array<keyof ExerciseFormData>; label: string }[] = [
  { fields: [], label: 'Type' },
  { fields: [], label: 'Caution' },
  { fields: ['title', 'description', 'timezone', 'startTime', 'endTime'], label: 'Overview' },
  { fields: ['simulator', 'visibility'], label: 'Exercise' },
  { fields: ['users'], label: 'Students' },
];
// Users without group-simulation access never see the Type step, so Overview becomes step 0.
const restrictedSteps = selfStudySteps.slice(1);

export const getScheduledSteps = (canViewGroupSimulations: boolean, type: ExerciseType) => {
  if (!canViewGroupSimulations) {
    return restrictedSteps;
  }
  return type === ExerciseType.Classroom ? classroomSteps : selfStudySteps;
};

const classroomStepComponents = [TypeStep, CautionStep, OverviewStep, ExerciseStep, StudentsStep];
const selfStudyStepComponents = [TypeStep, OverviewStep, ExerciseStep, StudentsStep];
const restrictedStepComponents = selfStudyStepComponents.slice(1);

export const getScheduledStepComponents = (canViewGroupSimulations: boolean, type: ExerciseType) => {
  if (!canViewGroupSimulations) {
    return restrictedStepComponents;
  }
  return type === ExerciseType.Classroom ? classroomStepComponents : selfStudyStepComponents;
};

const CreateExerciseDialog = ({ onConfirm, onClose, open }: CreateExerciseDialogProps) => {
  const [activeStep, setActiveStep] = useState(0);

  const form = useForm<ExerciseFormData>({
    defaultValues: {
      collectionId: null,
      description: '',
      endTime: null,
      exercisesIds: null,
      exercisesMetaData: [],
      layout: null,
      limitNumberOfAttempts: false,
      maxNumberOfAttempts: '',
      moduleId: null,
      studentEnvironment: {},
      simulator: '',
      startTime: new Date(),
      timezone: '',
      title: '',
      type: ExerciseType.SelfStudy,
      users: [],
      vessel: null,
      visibility: false,
    },
  });

  const [type] = form.watch(['type']);
  const canViewGroupSimulations = hasGroupSimulationAccess();
  const { simulators } = useSimulators(type);

  const steps = getScheduledSteps(canViewGroupSimulations, type);
  const stepNames = steps.map(step => step.label);

  const {
    createExercise,
    createExerciseError,
    isCreatingExercise,
    setError,
    setShowCreateExerciseError,
    showCreateExerciseError,
  } = useCreateExercise(form.setError, onConfirm);

  const handleNext: SubmitHandler<ExerciseFormData> = (data, e) => {
    e?.preventDefault();
    if (activeStep < stepNames.length - 1) {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    } else {
      createExercise({ ...data, adHoc: false });
    }
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  useEffect(() => {
    if (open) {
      form.reset();
      setActiveStep(0);
      setShowCreateExerciseError(false);
      setError(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (type) {
      form.setValue('exercisesIds', null);
    }
  }, [type]);

  const stepComponents = getScheduledStepComponents(canViewGroupSimulations, type);
  const StepComponent = stepComponents[activeStep];

  return (
    <Dialog open={open} fullWidth onClose={(_, reason) => onClose(reason)}>
      <DialogTitle>Schedule an exercise</DialogTitle>
      <StepComponent
        {...form}
        actions={
          <CreateExerciseActions
            activeStep={activeStep}
            finishLabel="Schedule exercise"
            isLoading={isCreatingExercise}
            handleBack={handleBack}
            handleCancel={onClose}
            simulators={simulators ?? []}
            steps={stepNames}
            type={type}
          />
        }
        handleNext={handleNext}
        isLoading={isCreatingExercise}
        simulators={simulators}
        stepper={
          <CreateExerciseStepper
            activeStep={activeStep}
            errors={form.formState.errors}
            steps={steps}
            setActiveStep={setActiveStep}
          />
        }
      />
      {showCreateExerciseError && <CreateExerciseError error={createExerciseError} />}
    </Dialog>
  );
};

export default CreateExerciseDialog;
